import {
  DataIQProfilePromptType,
  CandidateInsightsProfilePromptType,
} from '../llmResponseSchemas.js';
import { DateTimeSchema, DurationLikeObject } from '../dates.js';
import z from 'zod';
import * as R from 'remeda';
import { SimpleObjectIdString } from '../common.js';
import { ActivityType, CandidateActivity } from '../../interfaces.js';
import { zx } from '@traversable/zod';

export const BullhornApiEntityName = z.enum([
  'JobOrder',
  'Candidate',
  'CorporateUser',
  'Recruiter',
  'JobSubmission',
  'Note',
]);

//--------------------------------------------------------------------------------------------------------------------//
//region DataIQ config Schemas
export const DataIqFieldMappingMode = z
  .enum(['exactValue', 'llm'])
  .describe(
    'Which field mapping mode to use. `exactValue` will use the value from profileField exactly, skipping the LLM.'
  );
export type DataIqFieldMappingMode = z.infer<typeof DataIqFieldMappingMode>;

export const DataIqListMode = z
  .enum(['append', 'replace', 'fillBlank'])
  .describe(
    'How to handle field updates. `append`: add to existing list (multi-value only), `replace`: overwrite existing value, `fillBlank`: only write if current value is empty.'
  );
export type DataIqListMode = z.infer<typeof DataIqListMode>;

// No .default()s here: zod 4's .partial() doesn't suppress them, so parses would inject values the user never chose.
export const DataIQFieldConfigOptions = z
  .object({
    restrictSpecialtiesToCandidateCategory: z
      .boolean()
      .describe(
        "If enabled, only allow specialties that match the candidate's category. Only applicable for the 'specialties' field."
      ),
    restrictSpecialtiesToUniqueNames: z
      .boolean()
      .describe(
        "If enabled, prevent associating specialties that have the same name as one already on the candidate record, even if the ID differs. Only applicable for the 'specialties' field."
      ),
    strictProfileField: z
      .boolean()
      .describe(
        'If enabled, we will only apply the update if the candidate profile has a valid value on the path set on `profileField`.'
      ),
    allowDuplicateWrites: z
      .boolean()
      .describe(
        'If enabled, DataIQ may re-write a value it has already written to this candidate before, even if a human later removed or changed it. Leave off (default) to avoid re-write feedback loops.'
      ),
    mappingMode: DataIqFieldMappingMode,
    listMode: DataIqListMode,
    disableEnumConstraint: z
      .boolean()
      .describe(
        'If enabled, use string type with available options in description instead of strict enum validation. Useful for fields with large option sets where strict enums cause LLM errors.'
      ),
    validateReturnedOptionsExist: z
      .boolean()
      .describe(
        "If enabled, validate that LLM-returned options exist in the field's available options. Invalid options will be filtered out with warnings."
      ),
    blankEquivalents: z
      .array(z.string())
      .optional()
      .describe(
        'Values to treat as "no selection" (e.g. "None Specified", "Please Select") and strip from this field rather than keep. Matched exactly; a "?"-corrupted variant of a listed value is also stripped.'
      ),
    addToProfile: z
      .boolean()
      .describe(
        "If enabled, the LLM will extract this field's value directly from the candidate's activity notes and messages. Use this for customer specific fields that aren't captured by the standard candidate profile."
      ),
  })
  .partial()
  .describe('Options that determine various field mapping behavior.');
export type DataIQFieldConfigOptions = z.infer<typeof DataIQFieldConfigOptions>;

export const DataIQFieldConfig = z.object({
  id: SimpleObjectIdString,
  enabled: z.boolean().default(true).describe('If enabled, DataIQ writes this field to the ATS.'),
  enabledSimulation: z
    .boolean()
    .default(false)
    .describe(
      'If enabled, this field runs in DataIQ eval/simulation. On its own (enabled=false) the field is simulation-only and is never written to the ATS.'
    ),
  atsField: z
    .string()
    .min(1, 'ATS Field is required')
    .describe('ATS field where our data will be sent'),
  atsSourceField: z
    .string()
    .optional()
    .describe('ATS field we check for the "current value", if different'),
  label: z
    .string()
    .default('')
    .describe(
      'Field label for our UI, exports, and the LLM. Should NOT include a lengthy description.'
    ),
  description: z
    .string()
    .default('')
    .describe('Field description. Combined with the label when sent to the LLM.'),
  options: DataIQFieldConfigOptions.nullish(),
  profileField: z
    .string()
    .default('')
    .describe('Specific field/path from the candidate profile, if applicable.'),
});
export type DataIQFieldConfig = z.infer<typeof DataIQFieldConfig>;

export const DataIQFieldConfigUpdate = zx.deepNoDefaults(DataIQFieldConfig);
export type DataIqFieldConfigUpdate = z.infer<typeof DataIQFieldConfigUpdate>;

// New mappings default to Simulation (eval-only) so a freshly-added field can't ship to production untested.
export const DataIQFieldConfigFormDefault = DataIQFieldConfig.extend({
  atsField: z.string().default(''),
  enabled: z.boolean().default(false),
  enabledSimulation: z.boolean().default(true),
});
export type DataIQFieldConfigFormDefault = z.infer<typeof DataIQFieldConfigFormDefault>;

export const DEFAULT_LOOKUP_CONFIGS = {
  incremental: {
    lookBack: { days: 7 },
    timeBetweenAttempts: { hours: 1 },
    timeBetweenRetrievals: { days: 1 },
  },
  historical: {
    lookBack: { years: 1 },
    timeBetweenAttempts: { hours: 1 },
    timeBetweenRetrievals: null,
  },
} as const satisfies Record<DataIQLookupType, DataIQLookupTypeConfigFull>;

export const DataIQLookupTypeConfig = z.object({
  lookBack: DurationLikeObject.nullable().default(null),
  timeBetweenAttempts: DurationLikeObject.nullable().default(null),
  timeBetweenRetrievals: DurationLikeObject.nullable().default(null),
  llmModelOverride: z.string().optional(),
});
export type DataIQLookupTypeConfig = z.infer<typeof DataIQLookupTypeConfig>;

// Full DataIQ lookup config when combined with our defaults
export const DataIQLookupTypeConfigFull = DataIQLookupTypeConfig.safeExtend({
  lookBack: DurationLikeObject,
});
export type DataIQLookupTypeConfigFull = z.infer<typeof DataIQLookupTypeConfigFull>;

const LookupTypes = z.object({
  incremental: DataIQLookupTypeConfig.partial().default({}),
  historical: DataIQLookupTypeConfig.partial().default({}),
});

export const DataIQLookupType = LookupTypes.keyof();
export type DataIQLookupType = z.infer<typeof DataIQLookupType>;

/**
 * Candidate filter for DataIQ processing restrictions.
 * - `values`: Match if field equals any of these values (OR between values)
 * - `newerThan`: For dates, match if field >= (now - duration)
 * - `olderThan`: For dates, match if field <= (now - duration)
 * All filters are ANDed together.
 */
export const CandidateFilter = z.object({
  field: z.string().describe('Field path to filter on (supports dot notation)'),
  values: z
    .union([z.string(), z.number()])
    .array()
    .optional()
    .describe('Values to match (OR logic between values)'),
  newerThan: DurationLikeObject.optional().describe(
    'For dates: match if field >= (now - duration)'
  ),
  olderThan: DurationLikeObject.optional().describe(
    'For dates: match if field <= (now - duration)'
  ),
});
export type CandidateFilter = z.infer<typeof CandidateFilter>;

export const DataIQConfig = z.object({
  enabled: z
    .boolean()
    .default(false)
    .describe('If enabled, DataIQ will write to the ATS for this integration.'),
  defaultProfilePromptType: DataIQProfilePromptType.optional().default('candidate_profile_data_iq'),
  mappings: DataIQFieldConfig.array().optional().default([]),
  lookupTypes: LookupTypes.default(LookupTypes.parse({})),
  candidateFilters: CandidateFilter.array()
    .optional()
    .default([])
    .describe(
      'Filters to restrict which candidates DataIQ processes. All filters must match (AND logic).'
    ),
});
export type DataIQConfig = z.infer<typeof DataIQConfig>;

// candidateFilters excluded from deepNoDefaults because it breaks DurationLikeObject's partial record
export const DataIQConfigUpdate = z.intersection(
  zx.deepNoDefaults(DataIQConfig.omit({ mappings: true, candidateFilters: true })),
  z.object({ candidateFilters: CandidateFilter.array().optional() })
);

// Active for the current context (writeback and/or simulation); not guaranteed `enabled: true`.
export type EnabledDataIqFieldConfig = DataIQFieldConfig;

//--------------------------------------------------------------------------------------------------------------------//
//region Activity Data Mapping Config

export const ActivityDataMappingConfig = z.object({
  id: SimpleObjectIdString,
  dataType: ActivityType.default('Note'),
  filter: z
    .object({
      action: z.string().nullish(),
      status: z.string().nullish(),
      comments: z.string().nullish(),
    })
    .catchall(z.union([z.number(), z.string(), z.boolean(), z.null(), z.undefined()]))
    .default({}),

  // TODO change to
  //    output: CandidateActivity.partial(),
  output: z.record(z.string(), z.unknown()).default({}),
  metadata: z
    .object({
      createdAt: DateTimeSchema.nullish(),
      updatedAt: DateTimeSchema.nullish(),
    })
    .default({}),
});
export const ActivityDataMappings = z.object({
  mappings: ActivityDataMappingConfig.array(),
});
export type ActivityDataMappings = z.infer<typeof ActivityDataMappings>;
export type ActivityDataMappingConfig = z.infer<typeof ActivityDataMappingConfig> & {
  output: Partial<CandidateActivity>;
};

/**
 * Job order statuses the tenant marked as filled or closed. Callers exclude these rather than
 * listing the open ones, so a status nobody has classified yet still shows up in search results.
 */
export function getClosedJobStatuses(mappings: readonly ActivityDataMappingConfig[]): string[] {
  return R.unique(
    mappings
      .filter((mapping) => mapping.dataType === 'Position' && mapping.output?.isCompleted === true)
      .map((mapping) => mapping.filter.status?.trim())
      .filter(R.isTruthy)
  );
}

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region TalentIQ Config

export const DepartmentKeyGroup = z.object({
  departmentsToMatch: z.string().array(),
  departmentsToAdd: z.string().array(),
});
export type DepartmentKeyGroup = z.infer<typeof DepartmentKeyGroup>;

export const TalentIQConfig = z.object({
  promptTypes: z
    .object({
      privateLabel: z.string(),
      promptType: CandidateInsightsProfilePromptType,
    })
    .array(),
  defaultPromptType: CandidateInsightsProfilePromptType.nullish(),
  restrictToUserDepartment: z.boolean().optional(),
  departmentKeys: z
    .record(z.string().describe('Entity name'), z.string().array().describe('rawData field list'))
    .nullish(),
  departmentKeyGroups: DepartmentKeyGroup.array().nullish(),
  askToroSystemPrompt: z.string().max(4000).optional(), // injected verbatim as a system message in TalentIQ card chats
  // `shortPrompt` labels of default Ask Toro prompt suggestions this account doesn't want offered
  hiddenPromptSuggestions: z.string().array().optional(),
});
export type TalentIQConfig = z.infer<typeof TalentIQConfig>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Chrome Extension Config

export const GeneratedFileFormat = z.enum(['csv', 'txt', 'docx']);
export type GeneratedFileFormat = z.infer<typeof GeneratedFileFormat>;

export const GENERATED_FILE_FORMAT_META = {
  csv: { ext: '.csv', mimeType: 'text/csv' },
  txt: { ext: '.txt', mimeType: 'text/plain' },
  docx: {
    ext: '.docx',
    mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  },
} as const satisfies Record<GeneratedFileFormat, { ext: string; mimeType: string }>;

// Schema that takes a URL-like value and normalizes it to a hostname
const NormalizedHostName = z.preprocess(normalizeHostName, z.hostname());

// Normalizes hostnames and silently removes invalid values
const LooseHostNameList = z
  .array(NormalizedHostName.nullable().catch(null))
  .transform((list) => list.filter(R.isTruthy));

export const ChromeExtensionConfig = z.object({
  maxExportEntities: z.number().int().positive().optional(), // soft per-chat entity guidance shown to the LLM
  maxCsvRows: z.number().int().positive().optional(), // hard CSV row backstop enforced in the guard, hidden from the LLM
  disableExports: z.boolean().optional(), // blanket off-switch, covers formats added later
  disabledExportFormats: GeneratedFileFormat.array().optional(), // subtractions when exports on
  blockedDomains: NormalizedHostName.array().optional(), // Ask Toro may not navigate to / screenshot / extract these hosts (or their subdomains)
  askToroSystemPrompt: z.string().max(4000).optional(), // injected verbatim as a system message in extension chats
  disableUserMemory: z.boolean().optional(), // opt out of per-user Ask Toro memory: updateMemory tool + prompt injection (on by default)
  disableOpportunities: z.boolean().optional(), // hide StaffIQ opportunities in the extension (shown by default, but only while StaffIQ is enabled)
  submissionEnabled: z.boolean().optional(), // opt IN to the submitCandidateToShortlist write tool (off by default - it writes to the ATS)
  submissionStatus: z
    .string()
    .refine((status) => status.trim().toLowerCase() !== 'new lead', {
      message: 'Bullhorn treats the "New Lead" status as a web response; pick another status.',
    })
    .optional(), // JobSubmission.status written on every shortlisting
  submissionSource: z.string().optional(), // JobSubmission.source written on every shortlisting
});
export type ChromeExtensionConfig = z.infer<typeof ChromeExtensionConfig>;

// Single source for allowed formats — feeds both the tool definition and the guard so they can't drift.
export function resolveAllowedFormats(cfg?: ChromeExtensionConfig): GeneratedFileFormat[] {
  if (cfg?.disableExports) return [];
  const disabled = new Set(cfg?.disabledExportFormats ?? []);
  return GeneratedFileFormat.options.filter((f) => !disabled.has(f)); // undefined/[] -> all
}

// Reduce a manager-entered value (bare domain, full URL, mixed case, trailing dot) to a bare hostname.
function normalizeHostName(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  if (!s) return null;
  try {
    const host = new URL(s.includes('://') ? s : `https://${s}`).hostname
      .replace(/\.$/, '') // trailing-dot FQDN
      .replace(/^\*?\./, '') // "*.foo.com" / ".foo.com" — subdomains match anyway, so honor the intent
      .replace(/^www\./, '');
    // Drop single-label results (e.g. "com", or "www.com" -> "com") so a stray entry can't block a whole TLD.
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

// Normalized blocklist — single source for the delivery endpoint and the extension guard.
export function resolveBlockedDomains(cfg?: ChromeExtensionConfig): string[] {
  return LooseHostNameList.parse(cfg?.blockedDomains ?? []);
}

// Extension-facing config: full config with blockedDomains normalized + always present. One endpoint
// returns it and the extension caches it whole, so a new extension-consumed option needs no new endpoint/key.
export const ChromeExtensionIntegrationConfigDTO = ChromeExtensionConfig.safeExtend({
  blockedDomains: LooseHostNameList.default([]),
  // Resolved server-side from StaffIQ + disableOpportunities. The default lets the extension's cached-config parse of {} succeed
  opportunitiesEnabled: z.boolean().default(true),
}).prefault({});
export type ChromeExtensionIntegrationConfigDTO = z.infer<
  typeof ChromeExtensionIntegrationConfigDTO
>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region Advanced Config

/** @deprecated */
export const EntityFields = z.partialRecord(BullhornApiEntityName, z.string().array());
/** @deprecated */
export type EntityFields = z.infer<typeof EntityFields>;

export const AdvancedConfig = z.object({
  entityFields: EntityFields,
});
export type AdvancedConfig = z.infer<typeof AdvancedConfig>; // TODO: migrate existing Regex literals on prod and dev and remove support for those

//endregion
//--------------------------------------------------------------------------------------------------------------------//
